import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Delete, Get, Post } from "@repo/utils/api";
import UArray from "@repo/utils/UArray";
import UBoolean from "@repo/utils/UBoolean";
import UProxy from "@repo/utils/UProxy";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import USolid from "@repo/utils-solid/USolid";
import Booking from "@repo/vygor-models/Booking";

import { api } from "..";

import { Bookable, useBookables } from "./bookables";
import { Customer, useCustomers } from "./customers";
import { Organization, useOrganizations } from "./organizations";
import { Staff, useStaffs } from "./staffs";

export { Booking };

export type BookingsRest = Post<
  {
    bookings: UArray.WithLengthOf.AtLeast<Booking.Params[], 1>;
  },
  { bookings: Booking[]; customer: Customer }
> &
  Get<
    {},
    {
      bookings: Booking[];
      bookables: Bookable[];
      organizations: Organization[];
      staffProfiles: Staff[];
      bookableTemplates: Bookable.Template[];
    }
  > & {
    [id: Booking.Id]: Get<
      {},
      {
        booking: Booking;
        bookable: Bookable;
        organization: Organization;
        staffProfile?: Staff;
        bookableTemplate: Bookable.Template;
      }
    > &
      Delete<{ reason: string }>;
  };

const initialState = {
  byId: {} as Record<Booking.Id, Booking>,
  upcoming: null as Booking.Id[] | null,
};

export const { Context, Provider, useBookings } = FeatureStore.init(
  "Bookings",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const [, setOrganizations] = useOrganizations();
    const [bookables, setBookables] = useBookables();
    const [customers, setCustomers] = useCustomers();
    const [, setStaffs] = useStaffs();

    const accessors = {
      byId: attachAction(
        (id: Booking.Id | undefined) =>
          id &&
          store.byId[id] &&
          UProxy.merge(store.byId[id], {
            get bookable() {
              return bookables.byId(store.byId[id]!.bookableId);
            },
            get name() {
              return this.bookable?.name;
            },
          }),
        (id) => id && actions.fetchSingle(id),
        (id) => (res) => !id || res,
      ),
      upcoming: attachAction(
        () => store.upcoming?.map((id) => store.byId[id]!),
        () => actions.fetchUpcoming(),
      ),
    };

    const mutations = {
      one(booking: Booking) {
        setStore("byId", booking.id, reconcile(booking));
      },
      many(bookings: Booking[]) {
        batch(() => {
          for (const booking of bookings) {
            mutations.one(booking);
          }
        });
      },
      upcoming(bookings: (Booking | Booking.Id)[]) {
        setStore(
          "upcoming",
          bookings.map((booking) =>
            typeof booking === "string" ? booking : booking.id,
          ),
        );
      },
      addUpcoming(...bookings: Booking[]) {
        mutations.upcoming([
          ...(store.upcoming ?? []),
          ...bookings.map(({ id }) => id),
        ]);
      },
    };

    const actions = withLogger("action", {
      one: mutations.one,
      many: mutations.many,
      fetchSingle(id: Booking.Id) {
        return api.bookings[id]!.GET({}).then(
          ({
            booking,
            bookable,
            organization,
            staffProfile,
            bookableTemplate,
          }) => {
            batch(() => {
              setOrganizations.one(organization);
              setBookables.one(bookable);
              mutations.one(booking);
              if (staffProfile) setStaffs.one(staffProfile);
              setBookables.templates.one(bookableTemplate);
            });
          },
        );
      },
      fetchUpcoming() {
        return api.bookings
          .GET({})
          .then(
            ({
              bookings,
              bookables,
              organizations,
              staffProfiles,
              bookableTemplates,
            }) => {
              batch(() => {
                setOrganizations.many(organizations);
                setBookables.many(bookables);
                mutations.many(bookings);
                mutations.upcoming(bookings);
                setStaffs.many(staffProfiles);
                setBookables.templates.many(bookableTemplates);
              });
            },
          );
      },
      create(bookings: UArray.WithLengthOf.AtLeast<Booking.Params[], 1>) {
        return api.bookings.POST({ bookings }).then(async (res) => {
          const { bookings, customer } = res;
          const ownCustomerId = await USolid.Accessor.matchValue(
            () =>
              customers.forOrganization.own(
                bookables.byId(bookings[0]!.bookableId)!.organizationId,
              )?.id,
            UBoolean.isTruthy,
          );

          batch(() => {
            mutations.many(bookings);
            mutations.addUpcoming(
              ...bookings.filter(
                ({ customerId }) => customerId === ownCustomerId,
              ),
            );
            setCustomers.one(customer);
          });

          return res;
        });
      },
    });

    return { accessors, actions };
  },
);
